import React, { useRef, useState } from "react";
import styles from "./FeaturedEvents.module.css";
import Image from "next/image";
import dynamic from "next/dynamic";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

// Load lucide icons on client to avoid barrel optimizer runtime issue
const CalendarDays = dynamic(
  () => import("lucide-react").then((m) => m.CalendarDays),
  { ssr: false }
);
const Globe = dynamic(() => import("lucide-react").then((m) => m.Globe), {
  ssr: false,
});
const MapPin = dynamic(() => import("lucide-react").then((m) => m.MapPin), {
  ssr: false,
});
const ChevronLeft = dynamic(
  () => import("lucide-react").then((m) => m.ChevronLeft),
  { ssr: false }
);
const ChevronRight = dynamic(
  () => import("lucide-react").then((m) => m.ChevronRight),
  { ssr: false }
);

const FeaturedEvents = ({ data }: any) => {
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const swiperRef = useRef<any>(null);

  const handleSlideChange = (swiper: any) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerWithNavigation}>
        <div></div>
        <h2 className={styles.title}>{data.title}</h2>
        {data?.feature_event_cards?.length > 3 ? (
          <div className={styles.navigationButtons}>
            <button
              className={`${styles.navButton} ${styles.prevButton} ${
                isBeginning ? styles.disabled : ""
              }`}
              aria-label="Previous events"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              className={`${styles.navButton} ${styles.nextButton} ${
                isEnd ? styles.disabled : ""
              }`}
              aria-label="Next events"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        ) : (
          <div></div>
        )}
      </div>

      {data?.feature_event_cards?.length > 3 ? (
        <div className={styles.swiperContainer}>
          <Swiper
            slidesPerView={4}
            spaceBetween={32}
            modules={[Pagination, Navigation]}
            pagination={{ clickable: true, type: "bullets" }}
            navigation={{
              prevEl: `.${styles.prevButton}`,
              nextEl: `.${styles.nextButton}`,
            }}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            onSlideChange={handleSlideChange}
            className={styles.swiperStyle}
            breakpoints={{
              320: { slidesPerView: 1, spaceBetween: 24 },
              768: { slidesPerView: 2, spaceBetween: 24 },
              992: { slidesPerView: 3, spaceBetween: 32 },
              1200: { slidesPerView: 3, spaceBetween: 32 },
            }}
          >
            {data.feature_event_cards.map((event: any) => {
              const hasLink = event.type_link && event.type_link.url;
              const linkUrl = hasLink ? event.type_link.url : "#";

              const title = event?.event_card?.title || "";
              const truncatedTitle =
                title.length > 62 ? title.substring(0, 62) + "..." : title;

              const cardContent = (
                <>
                  <Image
                    src={event.image.url}
                    alt={event.image.alternativeText || event.event_card.title}
                    width={387}
                    height={200}
                    className={styles.cardImgTop}
                  />
                  <div className={`${styles.cardBody}`}>
                    {event.type_link && event.type_link.label && (
                      <div className={styles.eventType}>
                        {event.type_link.label}
                      </div>
                    )}
                    <div className="d-flex justify-content-between">
                      <span className="d-flex align-items-center gap-2">
                        <CalendarDays size={20} color="#18463E" />
                        {new Date(
                          event.event_card.date_time
                        ).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </span>
                      <span className="d-flex align-items-center gap-2">
                        <Image
                          src="/assets/Clock.svg"
                          alt={"clock"}
                          width={20}
                          height={20}
                        />
                        {new Date(event.event_card.date_time)
                          .toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })
                          .toLowerCase()}
                      </span>
                    </div>
                    <h5 className={styles.eventTitle}>{truncatedTitle}</h5>
                    <div className={styles.eventLocation}>
                      {event.event_card.location.toLowerCase() === "online" ? (
                        <Globe size={20} />
                      ) : (
                        <MapPin size={20} />
                      )}
                      <span className={styles.eventLocationTitle}>
                        {event.event_card.location}
                      </span>
                    </div>
                  </div>
                </>
              );

              return (
                <SwiperSlide key={event.id}>
                  {hasLink ? (
                    <Link href={linkUrl} className={styles.cardLink}>
                      <div
                        className={`${styles.card} ${
                          hasLink ? styles.clickable : ""
                        }`}
                      >
                        {cardContent}
                      </div>
                    </Link>
                  ) : (
                    <div className={styles.card}>{cardContent}</div>
                  )}
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      ) : (
        <div className="row">
          {data.feature_event_cards.map((event: any) => {
            // Determine if the card should be clickable and what URL to use
            const hasLink = event.type_link && event.type_link.url;
            const linkUrl = hasLink ? event.type_link.url : "#";

            // Create the card content
            const cardContent = (
              <>
                <Image
                  src={event.image.url}
                  alt={event.image.alternativeText || event.event_card.title}
                  width={387}
                  height={200}
                  className={styles.cardImgTop}
                />
                <div className={`${styles.cardBody}`}>
                  {/* Event type badge if available */}
                  {event.type_link && event.type_link.label && (
                    <div className={styles.eventType}>
                      {event.type_link.label}
                    </div>
                  )}

                  <div className="d-flex justify-content-between">
                    <span className="d-flex align-items-center gap-2">
                      <CalendarDays size={20} color="#18463E" />
                      {new Date(event.event_card.date_time).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )}
                    </span>

                    <span className="d-flex align-items-center gap-2">
                      <Image
                        src="/assets/Clock.svg"
                        alt={"clock"}
                        width={20}
                        height={20}
                      />
                      {new Date(event.event_card.date_time)
                        .toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })
                        .toLowerCase()}
                    </span>
                  </div>
                  {(() => {
                    const title = event?.event_card?.title || "";
                    const truncatedTitle =
                      title.length > 62
                        ? title.substring(0, 62) + "..."
                        : title;
                    return (
                      <h5 className={styles.eventTitle}>{truncatedTitle}</h5>
                    );
                  })()}
                  <div className={styles.eventLocation}>
                    {event.event_card.location.toLowerCase() === "online" ? (
                      <Globe size={20} />
                    ) : (
                      <MapPin size={20} />
                    )}
                    <span className={styles.eventLocationTitle}>
                      {event.event_card.location}
                    </span>
                  </div>
                </div>
              </>
            );

            return (
              <div className="col-md-4 mb-4 mt-4" key={event.id}>
                {hasLink ? (
                  <Link href={linkUrl} className={styles.cardLink}>
                    <div
                      className={`${styles.card} ${
                        hasLink ? styles.clickable : ""
                      }`}
                    >
                      {cardContent}
                    </div>
                  </Link>
                ) : (
                  <div className={styles.card}>{cardContent}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FeaturedEvents;
